import { BX, resolve } from '../internal/global';

/**
 * `BX.ajax`: the low-level XHR transport and the loaders built on it.
 *
 * Transcribed from `core_ajax.js`, whose callers - several hundred of them - depend on what it
 * does rather than on what it should do: status 0 counts as success, a temporary `Setup()` is spent
 * by the next request even when that request overrides it, a null member of the data serializes to
 * a lone `&`. `test/old/core/ajax-transport.test.ts` pins that behaviour, and the code below keeps
 * the loose comparisons and implicit conversions it relies on.
 *
 * The members are assigned to the local function, not read back off `BX.ajax`: the module is
 * evaluated before anything publishes it. Inside the members every call goes through the global -
 * `BX.ajax.xhr()`, `BX.ajax.prepareData()`, `BX.*` - and is resolved when it runs. That late
 * binding is the contract: tests replace `BX.ajax.xhr` and `BX.ajax` itself, consumers patch
 * members, and `actions.ts`, `forms.ts` and `page.ts` complete the object after this module.
 */

export interface LegacyAjax
{
	(config: any): any;
	[member: string]: any;
}

let tempDefaultConfig: Record<string, any> = {};
const defaultConfig: Record<string, any> = {
	method: 'GET', // request method: GET|POST
	dataType: 'html', // type of data loading: html|json|script
	timeout: 0, // request timeout in seconds. 0 for browser-default
	async: true, // whether request is asynchronous or not
	processData: true, // any data processing is disabled if false, only callback call
	scriptsRunFirst: false, // whether to run _all_ found scripts before onsuccess call. script tag can have an attribute "bxrunfirst" to turn  this flag on only for itself
	emulateOnload: true,
	skipAuthCheck: false, // whether to check authorization failure (SHOUD be set to true for CORS requests)
	start: true, // send request immediately (if false, request can be started manually via XMLHttpRequest object returned)
	cache: true, // whether NOT to add random addition to URL
	preparePost: true, // whether set Content-Type x-www-form-urlencoded in POST
	headers: false, // add additional headers, example: [{'name': 'If-Modified-Since', 'value': 'Wed, 15 Aug 2012 08:59:08 GMT'}, {'name': 'If-None-Match', 'value': '0'}]
	lsTimeout: 30, //local storage data TTL. useless without lsId.
	lsForce: false, //wheter to force query instead of using localStorage data. useless without lsId.
	/*
	other parameters:
		url: url to get/post
		data: data to post
		onsuccess: successful request callback. BX.proxy may be used.
		onfailure: request failure callback. BX.proxy may be used.
		onprogress: request progress callback. BX.proxy may be used.

		lsId: local storage id - for constantly updating queries which can communicate via localStorage. core_ls.js needed

	any of the default parameters can be overridden. defaults can be changed by BX.ajax.Setup() - for all further requests!
	*/
};
const loadedScripts: Record<string, any> = {};
const r = {
	// the octal range `\034-\254` of the source file, which TypeScript does not accept in a class
	// eslint-disable-next-line no-control-regex -- the range starts at a control character on purpose
	url_utf: /[^\x1C-\xAC]+/g,
	script_self: /\/bitrix\/js\/main\/core\/core(_ajax)*.js$/i,
	script_self_window: /\/bitrix\/js\/main\/core\/core_window.js$/i,
	script_self_admin: /\/bitrix\/js\/main\/core\/core_admin.js$/i,
	script_onload: /window.onload/g,
};

const getLastContentTypeHeader = function(headers: any): any
{
	if (!BX.Type.isArray(headers))
	{
		return null;
	}

	const lastHeader = headers
		// eslint-disable-next-line unicorn/prefer-array-find -- findLast() is newer than the browsers this file ran in
		.filter((header: any) => {
			return header.name === 'Content-Type';
		})
		.pop()
	;

	return lastHeader ? lastHeader.value : null;
};

// low-level method
export const ajax: LegacyAjax = function(config: any): any
{
	let status;

	if (!config || !config.url || !BX.type.isString(config.url))
	{
		return false;
	}

	for (const i in tempDefaultConfig)
	{
		if (typeof (config[i]) === 'undefined')
		{
			config[i] = tempDefaultConfig[i];
		}
	}

	tempDefaultConfig = {};

	for (const i in defaultConfig)
	{
		if (typeof (config[i]) === 'undefined')
		{
			config[i] = defaultConfig[i];
		}
	}

	config.method = config.method.toUpperCase();

	if (!BX.localStorage)
	{
		config.lsId = null;
	}

	if (BX.browser.IsIE())
	{
		let result = r.url_utf.exec(config.url);
		if (result)
		{
			do
			{
				config.url = config.url.replace(result, BX.util.urlencode(result));
				result = r.url_utf.exec(config.url);
			} while (result);
		}
	}

	if (config.dataType == 'json')
	{
		config.emulateOnload = false;
	}

	if (!config.cache && config.method == 'GET')
	{
		config.url = BX.ajax._uncache(config.url);
	}

	if (config.method == 'POST')
	{
		if (config.preparePost)
		{
			config.data = BX.ajax.prepareData(config.data);
		}
		else if (getLastContentTypeHeader(config.headers) === 'application/json')
		{
			const isJson = (
				BX.Type.isPlainObject(config.data)
				|| BX.Type.isString(config.data)
				|| BX.Type.isNumber(config.data)
				|| BX.Type.isBoolean(config.data)
				|| BX.Type.isArray(config.data)
			);

			if (isJson)
			{
				config.data = JSON.stringify(config.data);
			}
		}
	}

	let bXHR = true;
	if (config.lsId && !config.lsForce)
	{
		const v = BX.localStorage.get('ajax-' + config.lsId);
		if (v !== null)
		{
			bXHR = false;

			const lsHandler = function(lsData: any): void
			{
				if (lsData.key == 'ajax-' + config.lsId && lsData.value != 'BXAJAXWAIT')
				{
					const data = lsData.value;
					const bRemove = !!lsData.oldValue && data == null;
					if (!bRemove)
					{
						BX.ajax.__run(config, data);
					}
					else if (config.onfailure)
					{
						config.onfailure('timeout');
					}

					BX.removeCustomEvent('onLocalStorageChange', lsHandler);
				}
			};

			if (v == 'BXAJAXWAIT')
			{
				BX.addCustomEvent('onLocalStorageChange', lsHandler);
			}
			else
			{
				setTimeout(() => {
					lsHandler({ key: 'ajax-' + config.lsId, value: v });
				}, 10);
			}
		}
	}

	if (bXHR)
	{
		config.xhr = BX.ajax.xhr();
		if (!config.xhr)
		{
			return undefined;
		}

		if (config.lsId)
		{
			BX.localStorage.set('ajax-' + config.lsId, 'BXAJAXWAIT', config.lsTimeout);
		}

		if (BX.Type.isFunction(config.onprogress))
		{
			BX.bind(config.xhr, 'progress', config.onprogress);
		}

		if (BX.Type.isFunction(config.onprogressupload) && config.xhr.upload)
		{
			BX.bind(config.xhr.upload, 'progress', config.onprogressupload);
		}

		config.xhr.open(config.method, config.url, config.async);

		if (!config.skipBxHeader && !BX.ajax.isCrossDomain(config.url))
		{
			config.xhr.setRequestHeader('Bx-ajax', 'true');
		}

		if (config.method == 'POST' && config.preparePost)
		{
			config.xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');
		}

		if (typeof (config.headers) === 'object')
		{
			for (let i = 0; i < config.headers.length; i++)
			{
				config.xhr.setRequestHeader(config.headers[i].name, config.headers[i].value);
			}
		}

		let bRequestCompleted = false;
		const onreadystatechange = config.xhr.onreadystatechange = function(additional?: any): void
		{
			if (bRequestCompleted)
			{
				return;
			}

			if (additional === 'timeout')
			{
				if (config.onfailure)
				{
					config.onfailure('timeout', '', config);
				}

				BX.onCustomEvent(config.xhr, 'onAjaxFailure', ['timeout', '', config]);

				config.xhr.onreadystatechange = BX.DoNothing;
				config.xhr.abort();

				if (config.async)
				{
					config.xhr = null;
				}
			}
			else if (config.xhr.readyState == 4 || additional == 'run')
			{
				status = BX.ajax.xhrSuccess(config.xhr) ? 'success' : 'error';
				bRequestCompleted = true;
				config.xhr.onreadystatechange = BX.DoNothing;

				if (status == 'success')
				{
					const authHeader = (!!config.skipAuthCheck || BX.ajax.isCrossDomain(config.url))
						? false
						: config.xhr.getResponseHeader('X-Bitrix-Ajax-Status')
					;

					if (!!authHeader && authHeader == 'Authorize')
					{
						if (config.onfailure)
						{
							config.onfailure('auth', config.xhr.status, config);
						}

						BX.onCustomEvent(config.xhr, 'onAjaxFailure', ['auth', config.xhr.status, config]);
					}
					else
					{
						const data = config.xhr.responseText;

						if (config.lsId)
						{
							BX.localStorage.set('ajax-' + config.lsId, data, config.lsTimeout);
						}

						BX.ajax.__run(config, data);
					}
				}
				else
				{
					if (config.onfailure)
					{
						config.onfailure('status', config.xhr.status, config);
					}

					BX.onCustomEvent(config.xhr, 'onAjaxFailure', ['status', config.xhr.status, config]);
				}

				if (config.async)
				{
					config.xhr = null;
				}
			}
		};

		if (config.async && config.timeout > 0)
		{
			setTimeout(() => {
				if (config.xhr && !bRequestCompleted)
				{
					onreadystatechange('timeout');
				}
			}, config.timeout * 1000);
		}

		if (config.start)
		{
			config.xhr.send(config.data);

			if (!config.async)
			{
				onreadystatechange('run');
			}
		}

		return config.xhr;
	}

	return undefined;
} as LegacyAjax;

ajax.xhr = function(): any
{
	if (window.XMLHttpRequest)
	{
		try
		{
			return new XMLHttpRequest();
		}
		catch (e)
		{}
	}
	else if ((window as any).ActiveXObject)
	{
		const ActiveXObject = (window as any).ActiveXObject;
		try
		{
			return new ActiveXObject('Msxml2.XMLHTTP.6.0');
		}
		catch (e)
		{}

		try
		{
			return new ActiveXObject('Msxml2.XMLHTTP.3.0');
		}
		catch (e)
		{}

		try
		{
			return new ActiveXObject('Msxml2.XMLHTTP');
		}
		catch (e)
		{}

		try
		{
			return new ActiveXObject('Microsoft.XMLHTTP');
		}
		catch (e)
		{}

		throw new Error('This browser does not support XMLHttpRequest.');
	}

	return null;
};

ajax.isCrossDomain = function(url: any, location?: any): boolean
{
	location = location || window.location;

	//Relative URL gets a current protocol
	if (url.indexOf('//') === 0)
	{
		url = location.protocol + url;
	}

	//Fast check
	if (url.indexOf('http') !== 0)
	{
		return false;
	}

	const link = window.document.createElement('a');
	link.href = url;

	return link.protocol !== location.protocol
		|| link.hostname !== location.hostname
		|| BX.ajax.getHostPort(link.protocol, link.host) !== BX.ajax.getHostPort(location.protocol, location.host)
	;
};

ajax.getHostPort = function(protocol: any, host: any): string
{
	const match = /:(\d+)$/.exec(host);
	if (match)
	{
		return match[1];
	}

	if (protocol === 'http:')
	{
		return '80';
	}
	else if (protocol === 'https:')
	{
		return '443';
	}

	return '';
};

ajax.__prepareOnload = function(scripts: any, ajax_session: any): void
{
	if (scripts.length > 0)
	{
		BX.ajax['onload_' + ajax_session] = null;

		for (let i = 0, len = scripts.length; i < len; i++)
		{
			if (scripts[i].isInternal)
			{
				scripts[i].JS = scripts[i].JS.replace(r.script_onload, 'BX.ajax.onload_' + ajax_session);
			}
		}
	}

	BX.CaptureEventsGet();
	BX.CaptureEvents(window, 'load');
};

ajax.__runOnload = function(ajax_session: any): void
{
	if (BX.ajax['onload_' + ajax_session] != null)
	{
		BX.ajax['onload_' + ajax_session].apply(window);
		BX.ajax['onload_' + ajax_session] = null;
	}

	const h = BX.CaptureEventsGet();

	if (h)
	{
		for (let i = 0; i < h.length; i++)
		{
			h[i].apply(window);
		}
	}
};

ajax.__run = function(config: any, data: any): void
{
	if (!config.processData)
	{
		if (config.onsuccess)
		{
			config.onsuccess(data);
		}

		BX.onCustomEvent(config.xhr, 'onAjaxSuccess', [data, config]);
	}
	else
	{
		data = BX.ajax.processRequestData(data, config);
	}
};

ajax._onParseJSONFailure = function(this: any, data: any): void
{
	this.jsonFailure = true;
	this.jsonResponse = data;
	this.jsonProactive = String(data).startsWith('[WAF]');
};

ajax.processRequestData = function(data: any, config: any): void
{
	let result;
	let scripts: any[] = [];
	let styles: any[] = [];
	let registrations: Record<string, string> = {};
	switch (config.dataType.toUpperCase())
	{
		case 'JSON':
		{
			const context = config.xhr || {};
			BX.addCustomEvent(context, 'onParseJSONFailure', BX.proxy(BX.ajax._onParseJSONFailure, config));
			result = BX.parseJSON(data, context);
			BX.removeCustomEvent(context, 'onParseJSONFailure', BX.proxy(BX.ajax._onParseJSONFailure, config));

			if (!!result && BX.type.isArray(result.bxjs))
			{
				for (let i = 0; i < result.bxjs.length; i++)
				{
					if (BX.type.isNotEmptyString(result.bxjs[i]))
					{
						scripts.push({
							isInternal: false,
							JS: result.bxjs[i],
							bRunFirst: config.scriptsRunFirst,
						});
					}
					else
					{
						scripts.push(result.bxjs[i]);
					}
				}
			}

			if (!!result && BX.type.isArray(result.bxcss))
			{
				styles = result.bxcss;
			}

			break;
		}

		case 'SCRIPT':
			scripts.push({ isInternal: true, JS: data, bRunFirst: config.scriptsRunFirst });
			result = data;
			break;

		default: // HTML
		{
			const ob = BX.processHTML(data, config.scriptsRunFirst);
			result = ob.HTML;
			scripts = ob.SCRIPT;
			styles = ob.STYLE;
			registrations = ob.REGISTRATIONS;
			break;
		}
	}

	if (styles.length > 0)
	{
		BX.loadCSS(styles);
	}

	let ajax_session: any = null;
	if (config.emulateOnload)
	{
		// eslint-disable-next-line unicorn/numeric-separators-style -- the literal of core_ajax.js, kept verbatim
		ajax_session = parseInt(String(Math.random() * 1000000));
		BX.ajax.__prepareOnload(scripts, ajax_session);
	}

	const cb = BX.defer(() => {
		if (config.emulateOnload)
		{
			BX.ajax.__runOnload(ajax_session);
		}

		BX.onCustomEvent(config.xhr, 'onAjaxSuccessFinish', [config]);
	});

	try
	{
		if (config.jsonFailure)
		{
			// eslint-disable-next-line no-throw-literal -- the failure object is what onfailure receives
			throw { type: 'json_failure', data: config.jsonResponse, bProactive: config.jsonProactive };
		}

		config.scripts = scripts;

		BX.ajax.processScripts(config.scripts, true);

		if (config.onsuccess)
		{
			config.onsuccess(result);
		}

		BX.onCustomEvent(config.xhr, 'onAjaxSuccess', [result, config]);

		BX.ajax.processScripts(config.scripts, false, cb, () => {
			// through the global: this source is built into core_ajax.js too, and an import would give
			// that bundle a storage of its own
			Object.entries(registrations).forEach(([name, namespace]) => {
				BX.Runtime.registerExtension({ name, namespace });
			});
		});
	}
	catch (e)
	{
		if (config.onfailure)
		{
			config.onfailure('processing', e);
		}

		BX.onCustomEvent(config.xhr, 'onAjaxFailure', ['processing', e, config]);
	}
};

// `beforeInline` runs once the external scripts have loaded, right before the inline ones.
ajax.processScripts = function(scripts: any, bRunFirst?: any, cb?: any, beforeInline?: () => void): void
{
	let scriptsExt: any[] = [];
	let scriptsInt = '';

	cb = cb || BX.DoNothing;

	for (let i = 0, length = scripts.length; i < length; i++)
	{
		if (typeof bRunFirst !== 'undefined' && bRunFirst != !!scripts[i].bRunFirst)
		{
			continue;
		}

		if (scripts[i].isInternal)
		{
			scriptsInt += ';' + scripts[i].JS;
		}
		else
		{
			scriptsExt.push(scripts[i].JS);
		}
	}

	scriptsExt = BX.util.array_unique(scriptsExt);
	const inlineScripts = function(): void {
		beforeInline?.();
		if (scriptsInt.length > 0)
		{
			BX.evalGlobal(scriptsInt);
		}
	};

	if (scriptsExt.length > 0)
	{
		BX.load(scriptsExt, () => {
			inlineScripts();
			cb();
		});
	}
	else
	{
		inlineScripts();
		cb();
	}
};

ajax.prepareData = function(arData: any, prefix?: any): string
{
	let data = '';
	if (BX.type.isString(arData))
	{
		data = arData;
	}
	else if (arData != null)
	{
		for (const i in arData)
		{
			if (arData.hasOwnProperty(i))
			{
				if (data.length > 0)
				{
					data += '&';
				}

				let name = BX.util.urlencode(i);
				if (prefix)
				{
					name = prefix + '[' + name + ']';
				}

				if (typeof arData[i] === 'object')
				{
					data += BX.ajax.prepareData(arData[i], name);
				}
				else
				{
					data += name + '=' + BX.util.urlencode(arData[i]);
				}
			}
		}
	}

	return data;
};

ajax.xhrSuccess = function(xhr: any): boolean
{
	return (xhr.status >= 200 && xhr.status < 300) || xhr.status === 304 || xhr.status === 1223 || xhr.status === 0;
};

ajax.Setup = function(config: any, bTemp?: any): void
{
	bTemp = !!bTemp;

	for (const i in config)
	{
		if (bTemp)
		{
			tempDefaultConfig[i] = config[i];
		}
		else
		{
			defaultConfig[i] = config[i];
		}
	}
};

ajax.replaceLocalStorageValue = function(lsId: any, data: any, ttl: any): void
{
	if (BX.localStorage)
	{
		BX.localStorage.set('ajax-' + lsId, data, ttl);
	}
};

ajax._uncache = function(url: any): string
{
	return url + ((url.indexOf('?') !== -1 ? '&' : '?') + '_=' + Date.now());
};

/* simple interface */
ajax.get = function(url: any, data: any, callback?: any): any
{
	if (BX.type.isFunction(data))
	{
		callback = data;
		data = '';
	}

	data = BX.ajax.prepareData(data);

	if (data)
	{
		url += (url.indexOf('?') !== -1 ? '&' : '?') + data;
		data = '';
	}

	return BX.ajax({
		method: 'GET',
		dataType: 'html',
		url,
		data: '',
		onsuccess: callback,
	});
};

ajax.getCaptcha = function(callback: any): any
{
	return BX.ajax.loadJSON('/bitrix/tools/ajax_captcha.php', callback);
};

ajax.insertToNode = function(url: any, node: any): any
{
	node = resolve(node);
	if (node)
	{
		const eventArgs = { cancel: false };
		BX.onCustomEvent('onAjaxInsertToNode', [{ url, node, eventArgs }]);
		if (eventArgs.cancel === true)
		{
			return undefined;
		}

		let show: any = null;
		if (!tempDefaultConfig.denyShowWait)
		{
			show = BX.showWait(node);
			delete tempDefaultConfig.denyShowWait;
		}

		return BX.ajax.get(url, (data: any) => {
			node.innerHTML = data;
			BX.closeWait(node, show);
		});
	}

	return undefined;
};

ajax.post = function(url: any, data: any, callback: any): any
{
	data = BX.ajax.prepareData(data);

	return BX.ajax({
		method: 'POST',
		dataType: 'html',
		url,
		data,
		onsuccess: callback,
	});
};

/**
 * BX.ajax with BX.Promise
 */
ajax.promise = function(config: any): any
{
	const result = new BX.Promise();

	config.onsuccess = function(data: any): void
	{
		result.fulfill(data);
	};

	config.onfailure = function(reason: any, httpStatus: any, failedConfig: any): void
	{
		const ajaxConfig = failedConfig || config;

		result.reject({
			reason,
			data: httpStatus,
			ajaxConfig,
			xhr: ajaxConfig.xhr,
		});
	};

	const xhr = BX.ajax(config);
	if (xhr)
	{
		if (typeof config.onrequeststart === 'function')
		{
			config.onrequeststart(xhr);
		}
	}
	else
	{
		result.reject({
			reason: 'init',
			data: false,
		});
	}

	return result;
};

/* load and execute external file script with onload emulation */
ajax.loadScriptAjax = function(script_src: any, callback?: any, bPreload?: any): any
{
	if (BX.type.isArray(script_src))
	{
		for (let i = 0, len = script_src.length; i < len; i++)
		{
			BX.ajax.loadScriptAjax(script_src[i], callback, bPreload);
		}
	}
	else
	{
		const script_src_test = script_src.replace(/\.js\?.*/, '.js');

		if (r.script_self.test(script_src_test))
		{
			return undefined;
		}

		if (r.script_self_window.test(script_src_test) && BX.CWindow)
		{
			return undefined;
		}

		if (r.script_self_admin.test(script_src_test) && BX.admin)
		{
			return undefined;
		}

		if (typeof loadedScripts[script_src_test] === 'undefined')
		{
			if (bPreload)
			{
				loadedScripts[script_src_test] = '';

				return BX.loadScript(script_src);
			}
			else
			{
				return BX.ajax({
					url: script_src,
					method: 'GET',
					dataType: 'script',
					processData: true,
					emulateOnload: false,
					scriptsRunFirst: true,
					async: false,
					start: true,
					onsuccess(result: any): void {
						loadedScripts[script_src_test] = result;
						if (callback)
						{
							callback(result);
						}
					},
				});
			}
		}
		else if (callback)
		{
			callback(loadedScripts[script_src_test]);
		}
	}

	return undefined;
};

/* non-xhr loadings */
ajax.loadJSON = function(url: any, data: any, callback?: any, callback_failure?: any): any
{
	if (BX.type.isFunction(data))
	{
		callback_failure = callback;
		callback = data;
		data = '';
	}

	data = BX.ajax.prepareData(data);

	if (data)
	{
		url += (url.indexOf('?') !== -1 ? '&' : '?') + data;
		data = '';
	}

	return BX.ajax({
		method: 'GET',
		dataType: 'json',
		url,
		onsuccess: callback,
		onfailure: callback_failure,
	});
};
